import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import Tooltip from './ui/Tooltip';

interface WalletTrackingNumberProps {
  trackingNumber?: string | null;
  categoryName?: string | null;
  transactionName?: string | null;
  maxChars?: number;
  className?: string;
}

export const WalletTrackingNumber: React.FC<WalletTrackingNumberProps> = ({
  trackingNumber,
  categoryName,
  maxChars = 16,
  className = '',
}) => {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const category = categoryName?.toLowerCase();
  if (category === 'recharge') {
    return <span className="text-gray-400 font-normal text-xs">N/A</span>;
  }

  if (!trackingNumber || !trackingNumber.trim()) {
    return <span className="text-teal font-semibold text-xs">Bulk Order</span>;
  }

  const trackingList = trackingNumber
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  if (trackingList.length === 0) {
    return <span className="text-teal font-semibold text-xs">Bulk Order</span>;
  }

  const copyToClipboard = (text: string, index?: number, label?: string) => {
    navigator.clipboard.writeText(text);
    if (typeof index === 'number') {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1500);
    }
    toast.success(label ? `Copied: ${label}` : 'Tracking number copied');
  };

  // Case 1: Multiple tracking numbers (bulk upload debits, etc.)
  if (trackingList.length > 1) {
    const first = trackingList[0];
    const firstDisplay =
      first.length > maxChars ? `${first.slice(0, maxChars - 3)}...` : first;

    return (
      <Tooltip
        side="top"
        tooltipClassName="p-0 shadow-xl border border-gray-100 rounded-lg overflow-hidden bg-white"
        text={
          <div className="w-64 max-h-60 flex flex-col bg-white">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-700">
                Tracking Numbers
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono font-bold bg-teal/10 text-teal px-1.5 py-0.5 rounded-full">
                  {trackingList.length} total
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(trackingList.join(', '), undefined, 'All tracking numbers');
                  }}
                  className="text-[10px] text-teal hover:underline font-semibold"
                >
                  Copy All
                </button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-44 p-1 divide-y divide-gray-50">
              {trackingList.map((awb, i) => (
                <div
                  key={i}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyToClipboard(awb, i, awb);
                  }}
                  className="flex items-center justify-between px-2.5 py-1.5 text-xs font-mono text-gray-700 hover:bg-teal/5 hover:text-teal rounded cursor-pointer transition-colors group"
                  title="Click to copy"
                >
                  <span className="truncate">{awb}</span>
                  {copiedIndex === i ? (
                    <Check className="h-3 w-3 text-teal shrink-0 ml-2" />
                  ) : (
                    <Copy className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity ml-2 shrink-0" />
                  )}
                </div>
              ))}
            </div>
          </div>
        }
      >
        <div
          className={`inline-flex items-center justify-center gap-1.5 max-w-full group cursor-pointer ${className}`}
        >
          <span className="font-mono text-xs font-medium text-text group-hover:text-teal transition-colors truncate max-w-[90px] sm:max-w-[110px]">
            {firstDisplay}
          </span>
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-teal/10 text-teal border border-teal/20 shrink-0 group-hover:bg-teal group-hover:text-white transition-colors">
            +{trackingList.length - 1} more
          </span>
        </div>
      </Tooltip>
    );
  }

  // Case 2: Single tracking number (truncate if > maxChars)
  const single = trackingList[0];
  const isTruncated = single.length > maxChars;
  const displayVal = isTruncated
    ? `${single.slice(0, maxChars - 2)}...`
    : single;

  return (
    <Tooltip
      side="top"
      text={
        <div
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(single, 0, single);
          }}
          className="font-mono text-xs cursor-pointer hover:text-teal flex items-center gap-2 p-0.5 select-all"
          title="Click to copy"
        >
          <span>{single}</span>
          {copiedIndex === 0 ? (
            <Check className="h-3 w-3 text-teal shrink-0" />
          ) : (
            <Copy className="h-3 w-3 text-gray-400 hover:text-teal shrink-0" />
          )}
        </div>
      }
    >
      <span
        className={`font-mono text-xs text-text hover:text-teal transition-colors truncate max-w-[130px] inline-block align-middle cursor-pointer ${className}`}
      >
        {displayVal}
      </span>
    </Tooltip>
  );
};

export default WalletTrackingNumber;
